import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, centralApi } from '@marque/api/central'

const schema = z.object({
    displayName: z.string().min(1, 'Required'),
    slug: z.string().regex(/^[a-z0-9-]*$/, 'Only lowercase letters, numbers, and hyphens').optional(),
    adminEmail: z.string().email('Valid email required'),
    defaultCompanyName: z.string().min(1, 'Required'),
    provisioningMode: z.enum(['Shared', 'Dedicated']),
    localUsersEnabled: z.boolean(),
    // AB#1298 — optional bundle key; empty string means "core-default modules" (no bundle).
    bundleKey: z.string().optional(),
    // AB#2799 — optional first-admin role code; empty string means the tenant-owner default.
    bootstrapRoleCode: z.string().regex(/^[a-z0-9-]*$/, 'Only lowercase letters, numbers, and hyphens').optional(),
    // AB#1737 — optional default-company contact + address.
    companyPhone: z.string().optional(),
    companyAddressLine1: z.string().optional(),
    companyAddressLine2: z.string().optional(),
    companyCity: z.string().optional(),
    companyRegion: z.string().optional(),
    companyPostcode: z.string().optional(),
    companyCountry: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function Field({
    label,
    error,
    children,
    hint,
}: {
    label: string
    error?: string | undefined
    children: React.ReactNode
    hint?: string | undefined
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
            {children}
            {hint && !error && <p className="mt-1 text-xs text-neutral-400">{hint}</p>}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    )
}

const inputCls =
    'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

export function NewTenantPage() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: () => centralApi.listBundles() })
    const bundles = (bundlesQuery.data ?? []).filter(b => b.isActive)

    // AB#2931 — the module catalogue carries each module's declared pluggable backends; the
    // per-module dropdowns below render from it, never from a hard-coded list.
    const catalogQuery = useQuery({ queryKey: ['module-catalog'], queryFn: () => centralApi.listModuleCatalog() })
    const catalogById = new Map((catalogQuery.data ?? []).map(m => [m.moduleId, m]))

    // AB#2806 — tenant type: 'marque' (the ERP) or the key of an active STANDALONE bundle
    // (e.g. the ensured 'pricing-service' bundle renders as "Pricing Engine"). Choosing a
    // standalone type sets the bundle and prefills the bundle-declared bootstrap role, so the
    // operator no longer pairs bundleKey with bootstrapRoleCode by hand. UI-only state — what
    // is submitted is whatever ends up in the (still editable) Advanced fields below.
    // AB#3007 — 'custom' joins them: a NON-ERP tenant composed from an arbitrary set of standalone
    // modules. Bundles stay for the common shapes, but a bundle per sellable combination would need
    // 2^n of them, so the granular path is the one that makes the product composable.
    const [tenantType, setTenantType] = useState('marque')
    // AB#2931 — explicit backend choices, module id → name ('' / absent = the declared default).
    const [moduleBackends, setModuleBackends] = useState<Record<string, string>>({})
    // AB#3007 — the explicit module set for the 'custom' type. Dependency-expanded server-side.
    const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([])
    const standaloneBundles = bundles.filter(b => b.standalone)
    // AB#3007 — only modules the catalogue classifies standalone may be offered here. Filtering to
    // them is the difference between teaching the rule and letting provisioning refuse one attempt
    // at a time.
    const standaloneModules = (catalogQuery.data ?? [])
        .filter(m => m.isStandalone)
        .sort((a, b) => a.sortOrder - b.sortOrder)
    // AB#3007 — 'marque' is the only ERP type; every other choice composes a non-ERP tenant.
    const tenantKind = tenantType === 'marque' ? 'Erp' : 'NonErp'
    // Marque tenants pick from the ERP bundles (or core-default); standalone types may still be
    // overridden to any bundle from Advanced — the explicit selection wins.
    const bundleOptions = tenantType === 'marque' ? bundles.filter(b => !b.standalone) : bundles

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { provisioningMode: 'Shared', localUsersEnabled: true, bundleKey: '' },
    })

    // Switching type re-baselines the Advanced fields: back to Marque restores the ERP defaults
    // (no bundle, tenant-owner role); a standalone type prefills its bundle + declared role.
    function selectTenantType(type: string) {
        setTenantType(type)
        setModuleBackends({}) // AB#2931 — choices belong to the previous module set
        setSelectedModuleIds([]) // AB#3007 — likewise the module set itself

        if (type === 'marque') {
            setValue('bundleKey', '')
            setValue('bootstrapRoleCode', '')
        } else if (type === 'custom') {
            // AB#3007 — an explicit module set and a bundle are mutually exclusive server-side, so
            // choosing this path must clear any bundle the previous selection prefilled.
            setValue('bundleKey', '')
            setValue('bootstrapRoleCode', '')
        } else {
            const bundle = bundles.find(b => b.key === type)
            setValue('bundleKey', type)
            setValue('bootstrapRoleCode', bundle?.bootstrapRoleCode ?? '')
        }
    }

    // AB#3007 — toggling one module in the explicit set.
    function toggleModule(moduleId: string) {
        setSelectedModuleIds(prev =>
            prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId])
        setModuleBackends(prev => {
            const next = { ...prev }
            delete next[moduleId]
            return next
        })
    }

    // AB#2931 — which of the selected bundle's effective modules declare a backend choice.
    // The full ERP is forced onto every module's declared default (the Marque plugin) — the
    // dropdowns only render for a standalone product tenant.
    const selectedBundle = tenantType === 'marque' || tenantType === 'custom'
        ? undefined
        : bundles.find(b => b.key === tenantType)
    // AB#3007 — for the explicit path the module set IS the selection. Note this lists only the
    // modules the operator named: a dependency pulled in by the server-side closure that itself
    // declares backends is not offered a dropdown here, which is why provisioning still refuses a
    // missing required backend rather than trusting the screen to have asked.
    const pluggableModules = (tenantType === 'custom' ? selectedModuleIds : selectedBundle?.effectiveModules ?? [])
        .map(id => catalogById.get(id))
        .filter((m): m is NonNullable<typeof m> => !!m && (m.backends?.length ?? 0) > 0)

    const mutation = useMutation({
        mutationFn: (data: FormValues) =>
            centralApi.provisionTenant({
                displayName: data.displayName,
                slug: data.slug?.trim() || null,
                adminEmail: data.adminEmail,
                defaultCompanyName: data.defaultCompanyName,
                provisioningMode: data.provisioningMode,
                localUsersEnabled: data.localUsersEnabled,
                // AB#3007 — the choice above finally reaches the server. It has been on this screen
                // since AB#2806 but lived only in React state: every tenant an operator created was
                // provisioned as an ERP tenant regardless of which radio was selected.
                kind: tenantKind,
                // AB#3007 — the explicit set travels only on the custom path; naming both a module
                // set and a bundle is refused server-side.
                moduleIds: tenantType === 'custom' && selectedModuleIds.length > 0 ? selectedModuleIds : null,
                bundleKey: tenantType === 'custom' ? null : (data.bundleKey?.trim() ? data.bundleKey.trim() : null),
                bootstrapRoleCode: data.bootstrapRoleCode?.trim() ? data.bootstrapRoleCode.trim() : null,
                // AB#2931 — only EXPLICIT choices travel; unnamed modules run on their declared
                // default, and the ERP type sends nothing (defaults everywhere).
                moduleBackends: (() => {
                    const chosen = Object.entries(moduleBackends).filter(([, v]) => v !== '')
                    return tenantKind === 'NonErp' && chosen.length > 0 ? Object.fromEntries(chosen) : null
                })(),
                companyPhone: data.companyPhone?.trim() || null,
                companyAddressLine1: data.companyAddressLine1?.trim() || null,
                companyAddressLine2: data.companyAddressLine2?.trim() || null,
                companyCity: data.companyCity?.trim() || null,
                companyRegion: data.companyRegion?.trim() || null,
                companyPostcode: data.companyPostcode?.trim() || null,
                companyCountry: data.companyCountry?.trim() || null,
            }),
        onSuccess: async result => {
            await queryClient.invalidateQueries({ queryKey: ['tenants'] })
            await navigate({ to: '/tenants/$slug', params: { slug: result.slug } })
        },
    })

    return (
        <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-6 text-sm text-neutral-500">
                <Link to="/tenants" className="hover:text-neutral-900">Tenants</Link>
                <span>/</span>
                <span className="text-neutral-900">New tenant</span>
            </div>

            <h1 className="text-2xl font-semibold text-neutral-900 mb-8">Provision new tenant</h1>

            <form
                onSubmit={handleSubmit(data => mutation.mutate(data))}
                className="bg-white rounded-lg border border-neutral-200 p-6 space-y-5"
            >
                {/* AB#2806 — tenant type leads the form: what product is this tenant? One option
                    per active standalone bundle, labelled from the bundle name. */}
                <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Tenant type</label>
                    <div className="space-y-2" role="radiogroup" aria-label="Tenant type">
                        <label className="flex items-start gap-2 rounded-md border border-neutral-200 px-3 py-2 cursor-pointer hover:bg-neutral-50">
                            <input
                                type="radio"
                                name="tenantType"
                                className="mt-1"
                                checked={tenantType === 'marque'}
                                onChange={() => selectTenantType('marque')}
                                data-testid="tenant-type-marque"
                            />
                            <span>
                                <span className="block text-sm font-medium text-neutral-900">Marque tenant</span>
                                <span className="block text-xs text-neutral-400">
                                    The full ERP — core-default modules, or a commercial bundle chosen under Advanced.
                                </span>
                            </span>
                        </label>
                        {standaloneBundles.map(b => (
                            <label
                                key={b.key}
                                className="flex items-start gap-2 rounded-md border border-neutral-200 px-3 py-2 cursor-pointer hover:bg-neutral-50"
                            >
                                <input
                                    type="radio"
                                    name="tenantType"
                                    className="mt-1"
                                    checked={tenantType === b.key}
                                    onChange={() => selectTenantType(b.key)}
                                    data-testid={`tenant-type-${b.key}`}
                                />
                                <span>
                                    <span className="block text-sm font-medium text-neutral-900">{b.name} tenant</span>
                                    <span className="block text-xs text-neutral-400">
                                        Standalone product — {b.key} bundle
                                        {b.bootstrapRoleCode ? <> · first admin bootstraps as <code>{b.bootstrapRoleCode}</code></> : null}.
                                    </span>
                                </span>
                            </label>
                        ))}
                        {/* AB#3007 — the granular non-ERP path. A bundle per sellable combination
                            would need 2^n bundles, so this is the one that makes the product
                            composable; the bundles above stay for the shapes sold often. */}
                        <label className="flex items-start gap-2 rounded-md border border-neutral-200 px-3 py-2 cursor-pointer hover:bg-neutral-50">
                            <input
                                type="radio"
                                name="tenantType"
                                className="mt-1"
                                checked={tenantType === 'custom'}
                                onChange={() => selectTenantType('custom')}
                                data-testid="tenant-type-custom"
                            />
                            <span>
                                <span className="block text-sm font-medium text-neutral-900">Standalone (choose modules)</span>
                                <span className="block text-xs text-neutral-400">
                                    A non-ERP tenant composed from any combination of standalone modules.
                                    Dependencies are added automatically.
                                </span>
                            </span>
                        </label>
                    </div>
                </div>

                {/* AB#3007 — the module picker for the granular path. Only standalone-classified
                    modules appear: provisioning would refuse the others, and a picker that offers
                    them teaches the rule one failed attempt at a time. */}
                {tenantType === 'custom' && (
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Modules</label>
                        <div className="space-y-1.5 rounded-md border border-neutral-200 p-3">
                            {standaloneModules.length === 0 && (
                                <p className="text-sm text-neutral-500">
                                    No standalone modules in the catalogue. A module becomes selectable here by
                                    declaring <code>standalone: true</code> in its <code>module.json</code>.
                                </p>
                            )}
                            {standaloneModules.map(m => (
                                <label key={m.moduleId} className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={selectedModuleIds.includes(m.moduleId)}
                                        onChange={() => toggleModule(m.moduleId)}
                                        data-testid={`module-${m.moduleId}`}
                                    />
                                    <span>
                                        <span className="block text-sm text-neutral-900">
                                            {m.name}
                                            <span className="ml-1.5 text-xs text-neutral-400 font-mono">{m.moduleId}</span>
                                            {m.backendRequired && (
                                                <span className="ml-1.5 text-xs text-amber-600">needs a backend</span>
                                            )}
                                        </span>
                                        {m.description && (
                                            <span className="block text-xs text-neutral-400">{m.description}</span>
                                        )}
                                    </span>
                                </label>
                            ))}
                            <p className="text-xs text-neutral-400 pt-1">
                                Only the modules you tick, plus what they depend on. A module marked
                                &ldquo;needs a backend&rdquo; must be given one below — a non-ERP tenant has no
                                full-ERP default to fall back on.
                            </p>
                        </div>
                    </div>
                )}

                {/* AB#2931 — per-module backend choice for a standalone product tenant, one
                    dropdown per effective module that declares backends. Options come from the
                    module's own catalogue declaration. */}
                {pluggableModules.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Module backends</label>
                        <div className="space-y-2 rounded-md border border-neutral-200 p-3">
                            {pluggableModules.map(m => (
                                <div key={m.moduleId} className="flex items-center gap-3">
                                    <span className="text-sm text-neutral-700 flex-1">
                                        {m.name}
                                        <span className="ml-1.5 text-xs text-neutral-400 font-mono">{m.moduleId}</span>
                                    </span>
                                    <select
                                        value={moduleBackends[m.moduleId] ?? ''}
                                        onChange={e => setModuleBackends(prev => ({ ...prev, [m.moduleId]: e.target.value }))}
                                        className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                        data-testid={`backend-select-${m.moduleId}`}
                                    >
                                        {/* AB#3007 — a module that REQUIRES a backend gets no
                                            "Default" option in a non-ERP tenant, because there is
                                            no applicable default: defaultInFullErp is read only for
                                            an ERP tenant. Offering it here would let the operator
                                            choose an option that provisioning then refuses. */}
                                        {!(m.backendRequired && tenantKind === 'NonErp') && (
                                            <option value="">
                                                Default{m.defaultBackend ? ` (${m.defaultBackend})` : ''}
                                            </option>
                                        )}
                                        {m.backendRequired && tenantKind === 'NonErp' && (
                                            <option value="">Choose a backend…</option>
                                        )}
                                        {(m.backends ?? []).map(b => (
                                            <option key={b} value={b}>{b}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <p className="text-xs text-neutral-400">
                                Which system each module reads and writes for this tenant. Leave on Default unless
                                the customer runs against another backend (e.g. SAP during migration).
                            </p>
                        </div>
                    </div>
                )}

                <Field label="Display name" error={errors.displayName?.message}>
                    <input {...register('displayName')} className={inputCls} placeholder="Acme Corp" />
                </Field>

                <Field
                    label="Slug"
                    error={errors.slug?.message}
                    hint="Leave blank to auto-generate from the display name."
                >
                    <input
                        {...register('slug')}
                        className={inputCls}
                        placeholder="acme-corp"
                    />
                </Field>

                <Field label="Admin email" error={errors.adminEmail?.message}>
                    <input
                        {...register('adminEmail')}
                        type="email"
                        className={inputCls}
                        placeholder="admin@acme-corp.example"
                    />
                </Field>

                <Field label="Default company name" error={errors.defaultCompanyName?.message}>
                    <input
                        {...register('defaultCompanyName')}
                        className={inputCls}
                        placeholder="Acme Corp"
                    />
                </Field>

                <Field label="Telephone number" error={errors.companyPhone?.message}>
                    <input {...register('companyPhone')} className={inputCls} placeholder="+44 20 7946 0000" />
                </Field>

                <Field label="Address line 1" error={errors.companyAddressLine1?.message}>
                    <input {...register('companyAddressLine1')} className={inputCls} placeholder="1 High Street" />
                </Field>

                <Field label="Address line 2" error={errors.companyAddressLine2?.message}>
                    <input {...register('companyAddressLine2')} className={inputCls} />
                </Field>

                <Field label="City" error={errors.companyCity?.message}>
                    <input {...register('companyCity')} className={inputCls} placeholder="London" />
                </Field>

                <Field label="County / region" error={errors.companyRegion?.message}>
                    <input {...register('companyRegion')} className={inputCls} />
                </Field>

                <Field label="Postcode" error={errors.companyPostcode?.message}>
                    <input {...register('companyPostcode')} className={inputCls} placeholder="EC1A 1AA" />
                </Field>

                <Field label="Country" error={errors.companyCountry?.message}>
                    <input {...register('companyCountry')} className={inputCls} placeholder="United Kingdom" />
                </Field>

                <Field label="Provisioning mode" error={errors.provisioningMode?.message}>
                    <select {...register('provisioningMode')} className={inputCls}>
                        <option value="Shared">Shared</option>
                        <option value="Dedicated">Dedicated</option>
                    </select>
                </Field>

                {/* AB#2806 — bundle + bootstrap role moved into a collapsed Advanced disclosure:
                    the tenant type above prefills them; an explicit edit here still wins. */}
                <details className="rounded-md border border-neutral-200" data-testid="advanced-disclosure">
                    <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">
                        Advanced — module bundle &amp; bootstrap role
                    </summary>
                    <div className="space-y-5 border-t border-neutral-200 px-3 py-4">
                        <Field
                            label="Module bundle"
                            error={errors.bundleKey?.message}
                            hint={tenantType === 'marque'
                                ? 'Determines which modules the tenant is entitled to (plus their dependencies). Leave as core-default for the standard module set.'
                                : 'Prefilled from the tenant type. Changing it overrides the type’s bundle.'}
                        >
                            <select {...register('bundleKey')} className={inputCls} data-testid="bundle-select">
                                <option value="">Core-default modules (no bundle)</option>
                                {bundleOptions.map(b => (
                                    <option key={b.key} value={b.key}>{b.name}</option>
                                ))}
                            </select>
                        </Field>

                        <Field
                            label="Bootstrap admin role"
                            error={errors.bootstrapRoleCode?.message}
                            hint={tenantType === 'marque'
                                ? 'Role auto-assigned to the first admin at sign-in. Leave blank for tenant-owner.'
                                : 'Prefilled from the bundle’s declared role. An explicit value here always wins.'}
                        >
                            <input
                                {...register('bootstrapRoleCode')}
                                className={inputCls}
                                placeholder="tenant-owner"
                                data-testid="bootstrap-role-input"
                            />
                        </Field>
                    </div>
                </details>

                <div>
                    <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                        <input {...register('localUsersEnabled')} type="checkbox" className="rounded" />
                        Allow local users (username + password sign-in)
                    </label>
                    <p className="mt-1 text-xs text-neutral-400">
                        Uncheck to require IdP sign-in from day one. Can be changed later.
                    </p>
                </div>

                {/* AB#3007 — render the FIELD errors, not just the title. A ValidationProblem's title
                    is the generic "One or more validation errors occurred."; the reason a tenant was
                    refused — which module is not standalone, which one needs a backend nobody chose —
                    travels in `errors`. Showing only the title turned every refusal this ticket adds
                    into an unactionable message, which is the failure mode it exists to prevent. */}
                {mutation.isError && (
                    <div className="text-sm text-red-600 space-y-1" data-testid="provision-error">
                        {(() => {
                            const detail = mutation.error instanceof ApiError
                                ? Object.values(mutation.error.fieldErrors).flat()
                                : []
                            if (detail.length > 0) {
                                return detail.map((msg, i) => <p key={i}>{msg}</p>)
                            }
                            return (
                                <p>
                                    {mutation.error instanceof Error
                                        ? mutation.error.message
                                        : 'Failed to provision tenant.'}
                                </p>
                            )
                        })()}
                    </div>
                )}

                <div className="flex gap-3 pt-2">
                    <button
                        type="submit"
                        disabled={mutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                        {mutation.isPending ? 'Provisioning…' : 'Provision tenant'}
                    </button>
                    <Link
                        to="/tenants"
                        className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900"
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    )
}
